import { apiGet, apiPost } from './client.js';

export type PrintDriverPrinter = {
  id: number;
  name: string;
  state: string;
  computerName: string;
};

export async function listPrintPrinters(): Promise<PrintDriverPrinter[]> {
  return apiGet<PrintDriverPrinter[]>('/api/v1/print/printers').then((r) => r.data);
}

export async function printTicketLabel(ticketId: string, body?: { copies?: number }) {
  return apiPost<{ jobId: number; success: boolean }, { copies?: number }>(
    `/api/v1/print/label/${ticketId}`,
    body ?? {},
  ).then((r) => r.data);
}

export async function printTicketReceipt(ticketId: string, body?: { copies?: number }) {
  return apiPost<{ jobId: number; success: boolean }, { copies?: number }>(
    `/api/v1/print/receipt/${ticketId}`,
    body ?? {},
  ).then((r) => r.data);
}

export async function printPosReceipt(saleId: string, body?: { copies?: number }) {
  return apiPost<{ jobId: number; success: boolean }, { copies?: number }>(
    `/api/v1/print/receipt/pos/${saleId}`,
    body ?? {},
  ).then((r) => r.data);
}

export async function printTestLabel(body?: { copies?: number }) {
  return apiPost<{ jobId: number; success: boolean }, { copies?: number }>(
    '/api/v1/print/label/test',
    body ?? {},
  ).then((r) => r.data);
}

export async function printTestReceipt(body?: { copies?: number }) {
  return apiPost<{ jobId: number; success: boolean }, { copies?: number }>(
    '/api/v1/print/receipt/test',
    body ?? {},
  ).then((r) => r.data);
}

export type InventoryLabelPrintItem = { itemId: string; quantity?: number };

export type InventoryLabelPrintResult = {
  jobId?: number;
  success?: boolean;
  labelCount?: number;
  warnings?: string[];
  html?: string;
  browserFallback?: boolean;
  dimensions?: { widthIn: number; heightIn: number };
};

export async function printInventoryLabels(body: {
  items?: InventoryLabelPrintItem[];
  itemIds?: string[];
  copies?: number;
  htmlOnly?: boolean;
}) {
  return apiPost<InventoryLabelPrintResult, typeof body>('/api/v1/print/inventory/labels', body).then(
    (r) => r.data,
  );
}

/** Same endpoint as printInventoryLabels (legacy path). */
export async function printInventoryPoLabels(body: {
  items?: InventoryLabelPrintItem[];
  itemIds?: string[];
  copies?: number;
  htmlOnly?: boolean;
}) {
  return apiPost<InventoryLabelPrintResult, typeof body>(
    '/api/v1/print/inventory/po-labels',
    body,
  ).then((r) => r.data);
}

export async function previewInventoryLabels(body?: {
  itemId?: string;
  config?: Record<string, unknown>;
}) {
  return apiPost<
    {
      html: string;
      warnings: string[];
      dimensions: { widthIn: number; heightIn: number };
    },
    typeof body
  >('/api/v1/print/inventory/labels/preview', body ?? {}).then((r) => r.data);
}

export async function fetchInventoryLabelPreviewHtml(itemId?: string): Promise<string> {
  const res = await apiGet<{ html: string }>('/api/v1/print/inventory/labels/preview/html', {
    params: itemId ? { itemId } : undefined,
  });
  // GET returns raw HTML sometimes - when envelope
  if (typeof res === 'string') return res;
  if (res.data && typeof (res as { data?: { html?: string } }).data === 'object' && 'html' in (res as never)) {
    // envelope path not used for raw HTML endpoint
  }
  return '';
}

export async function fetchLabelPreviewHtml(ticketId: string): Promise<string> {
  const res = await apiGet<{ html: string }>(`/api/v1/print/label/${ticketId}/preview/html`);
  if (!res.data?.html?.trim()) {
    throw new Error('Empty label preview response');
  }
  return res.data.html;
}

export async function fetchReceiptPreviewHtml(ticketId: string): Promise<string> {
  const res = await apiGet<{ html: string }>(`/api/v1/print/receipt/${ticketId}/preview/html`);
  if (!res.data?.html?.trim()) {
    throw new Error('Empty receipt preview response');
  }
  return res.data.html;
}
